import {ref, nextTick, computed, shallowReactive, reactive} from "vue";
import validation from "@/components/validation";
import api from "@/api";
import { toast } from '@/lib/toast';
import i18n from '@/plugins/i18n';

export function useEditContragent() {
  const ORGANIZER = "organizer"
  const SUPPLIER = "supplier"

  const contragent = shallowReactive({
    type: ORGANIZER,
    fullName: null,
    inn: null,
    kpp: null,
    ogrn: null,
    email: null,
    phone: null,
    okved: null,
    categories: [],
  });

  //Ссылки на компоненты формы (ИНН и ОГРН) для валидации
  const contragentInnRef = ref(null);
  const contragentOgrnRef = ref(null);

  const rules = {
    fullName: [].concat(validation.required(), validation.text(1000)),
    inn: [].concat(validation.required(), validation.inn()),
    kpp: [].concat(validation.kpp(contragent)),
    ogrn: [].concat(validation.required(), validation.ogrn(contragent)),
    phone: [].concat(validation.phone()),
    email: [].concat(validation.email(), validation.text(255)),
    crn: [].concat(validation.required(), validation.crn()),
  };

  const masks = {
    inn: {
        mask: 'Y'.repeat(50),
        tokens: {
            'Y': {
                pattern: /[A-Za-z0-9\-\/. ]/,
            },
        },
    },
    kpp: {
      mask: '####YY###',
      tokens: {
        'Y': {
          pattern: /[0-9a-zA-Z]/,
          transform: v => v.toLocaleUpperCase()
        },
        '#': {pattern: /\d/}
      }
    },
    ogrn: '###############'
  }

  //Флаг обработки доступа к КПП (Если ИП, то false)
  const kppDisabled = ref(true);

  //Ограничитель для ОГРН по ИНН
  const maxOgrnLen = computed(() => {
    return contragent.inn?.length > 10 ? 15 : 13;
  });

  //Проверка шаблона КПП и ОГРН по ИНН
  const checkInn = () => {
    let lenInn = contragent.inn?.length;
    kppDisabled.value = lenInn !== 12;
    if (!kppDisabled.value) {
      contragent.kpp = null;
      nextTick(() => {
        contragentInnRef.value?.validate();
      });
    }
    let lenOgrn = contragent.ogrn?.length;
    if (lenOgrn > 0) {
      if (lenInn < 11 && lenOgrn > 13) {
        contragent.ogrn = contragent.ogrn.slice(0, 13);
      }
      nextTick(() => {
        contragentOgrnRef.value?.validate();
      });
    }
  };

  const fillContragentData = (newData = null) => {
    let oldData = JSON.parse(JSON.stringify(contragent));
    oldData.isOrganizer = contragent.type === ORGANIZER;
    oldData.isSupplier = contragent.type === SUPPLIER;
    oldData.categories = contragent.categories.map(c => c.id);
    if (newData) {
      Object.assign(contragent, newData);
      const type = getType(newData);
      if (type !== null) {
        contragent.type = type;
      }
    }
    kppDisabled.value = contragent.inn?.length !== 12;
    return oldData;
  }

  const getType = (data) => {
    return data?.isOrganizer ? ORGANIZER : (data?.isSupplier ? SUPPLIER : null);
  }

  return {
    ORGANIZER,
    SUPPLIER,
    contragent,
    rules,
    masks,
    kppDisabled,
    contragentInnRef,
    contragentOgrnRef,

    maxOgrnLen,

    checkInn,
    fillContragentData
  }
}

export function useDaDataContragent() {
  const firstAttemptDadata = ref(false);
  const isActiveDadata = ref(false)
  const loadDadataActive = async () => {
    let resp = null
    try {
      resp = await api.get(`/admin/feature/DADATA_FEATURE`);
      isActiveDadata.value = firstAttemptDadata.value = resp.feature[0].isActive;
      return resp.feature[0];
    } catch (e) {
      isActiveDadata.value = firstAttemptDadata.value = false;
      return null;
    }
  }
  const checkDadata = async (contragentInn) => {
    if (isActiveDadata.value) {
      let resp;
      const toastId = toast.loading('Загрузка данных...', {
        autoClose: false,
        closeOnClick: false,
        pauseOnFocusLoss: false,
        draggable: false,
        position: toast.POSITION.TOP_RIGHT
      });
      try {
        const formData = new FormData();
        formData.append('inn', contragentInn);
        resp = await api.post('/feature/lookupDaData', formData);
      } catch (e) {
        resp = false;
      }

      firstAttemptDadata.value = false;
      if (resp && resp.contragent) {
        toast.remove(toastId);
        let contr = resp.contragent;
        return {
          fullName: contr.value ?? null,
          kpp: contr.data.kpp ?? null,
          ogrn: contr.data.ogrn ?? null,
          okved: contr.data.okved ?? null
        }
      } else {
        toast.update(toastId, {
          render: "Данные не обнаружены во внешнем сервисе",
          type: 'error',
          isLoading: false,
          autoClose: 1000,
          closeOnClick: true,
          draggable: true
        });
      }
    }
  }

  return {
    checkDadata,
    loadDadataActive,
    firstAttemptDadata
  }
}

export function useHardPasswordRules() {
  let firstTry = true;
  const specialSymbols = /[!@#$%^&*()_+\-=\[\]{}|;':",.<>\/?]/;
  const GREY = 'grey_text';
  const RED = 'red_text';
  const GREEN = 'green_text';

  const exprRes = {
    true: GREEN,
    false: RED
  };

  // при передаче null-значения функция test() получает на вход текстовую строку 'null', поэтому проверки вида /[a-z]/.test(text) проходят успешно.
  const rules = reactive([
    {
      text: '8+ символов, ',
      class: GREY,
      func: text => text?.length > 7
    },
    {
      text: 'заглавная ',
      class: GREY,
      func: text => text?.length > 0 && /[A-Z]/.test(text)
    },
    {
      text: 'и ',
      class: GREY,
      func: text => text?.length > 0 && /[a-z]/.test(text) && /[A-Z]/.test(text)
    },
    {
      text: 'строчная ',
      class: GREY,
      func: text => text?.length > 0 && /[a-z]/.test(text)
    },
    {
      text: 'буквы латиницы, ',
      class: GREY,
      func: text => text?.length > 0 && (/[a-z]/.test(text) || /[A-Z]/.test(text))
    },
    {
      text: 'цифра, ',
      class: GREY,
      func: text => text?.length > 0 && /[0-9]/.test(text)
    },
    {
      text: 'спецсимвол',
      class: GREY,
      func: text => text?.length > 0 && specialSymbols.test(text)
    },
  ]);

  rules.forEach(rule => {
    rule.text = i18n.global.t(rule.text)
  })

  const validatePassword = (required = true) => [password => {
    const reqChecker = password?.length > 0 || required;
    if (!firstTry && reqChecker) {
      rules.forEach(rule => {
        const res = rule.func(password)
        rule.class = exprRes[res];
      });
    } else {
      firstTry = false;
    }
    return !reqChecker || rules.every(rule => rule.class === GREEN) || 'error';
  }]


  return {
    validatePassword,
    passwordRules: rules
  };
}